import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  LogOut, 
  Shield, 
  ShieldCheck, 
  ShieldOff, 
  Loader2, 
  User,
  QrCode,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const UserMenu = () => {
  const { user, logout, setup2FA, enable2FA, disable2FA } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      toast.success("Déconnexion réussie");
    } catch (e) {
      toast.error("Erreur lors de la déconnexion");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const data = await setup2FA();
      setQrData(data);
      setShowSetup2FA(true);
    } catch (e) {
      toast.error("Erreur lors de la configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (verifyCode.length !== 6) return;
    setLoading(true);
    try {
      await enable2FA(verifyCode);
      toast.success("Double authentification activée !");
      setShowSetup2FA(false);
      setQrData(null);
      setVerifyCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) return;
    setLoading(true);
    try {
      await disable2FA(disableCode);
      toast.success("Double authentification désactivée");
      setDisableCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="border-t border-slate-200 p-3 bg-slate-50">
      {/* User Info */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-medium">
          {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
        </div>
        {user.totp_enabled && (
          <ShieldCheck className="w-4 h-4 text-emerald-600" title="2FA activée" />
        )}
      </div>

      {/* 2FA Setup / Disable */}
      <div className="space-y-2 mb-3">
        {!user.totp_enabled ? (
          <Dialog open={showSetup2FA} onOpenChange={setShowSetup2FA}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSetup2FA}
                disabled={loading}
                className="w-full h-8 text-xs justify-start"
                data-testid="setup-2fa-btn"
              >
                <Shield className="w-3.5 h-3.5 mr-2" />
                Activer la double authentification
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Configurer la 2FA
                </DialogTitle>
              </DialogHeader>
              
              {qrData && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy, etc.)
                  </p>
                  
                  <div className="flex justify-center p-4 bg-white border rounded-lg">
                    <img src={qrData.qr_code} alt="QR Code 2FA" className="w-48 h-48" />
                  </div>
                  
                  <div className="bg-slate-50 p-3 rounded-md">
                    <p className="text-xs text-slate-500 mb-1">Code secret (si vous ne pouvez pas scanner) :</p>
                    <code className="text-xs font-mono bg-white px-2 py-1 rounded border block text-center">
                      {qrData.secret}
                    </code>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Entrez le code de vérification</Label>
                    <Input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-10 text-center text-lg tracking-widest font-mono mt-1"
                      placeholder="000000"
                      maxLength={6}
                      data-testid="enable-2fa-code-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleEnable2FA}
                    disabled={loading || verifyCode.length !== 6}
                    className="w-full h-9 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="enable-2fa-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Activer la 2FA
                      </>
                    )}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        ) : (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs justify-start text-amber-700 border-amber-200 hover:bg-amber-50"
                data-testid="disable-2fa-btn"
              >
                <ShieldOff className="w-3.5 h-3.5 mr-2" />
                Désactiver la double authentification
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <ShieldOff className="w-5 h-5" />
                  Désactiver la 2FA
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Entrez un code de votre application d'authentification pour confirmer la désactivation.
                </p>
                
                <div>
                  <Label className="text-sm">Code de vérification</Label>
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-10 text-center text-lg tracking-widest font-mono mt-1"
                    placeholder="000000"
                    maxLength={6}
                    data-testid="disable-2fa-code-input"
                  />
                </div>
                
                <Button
                  onClick={handleDisable2FA}
                  disabled={loading || disableCode.length !== 6}
                  variant="destructive"
                  className="w-full h-9"
                  data-testid="confirm-disable-2fa-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Désactiver la 2FA"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Logout */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        disabled={loading}
        className="w-full h-8 text-xs justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        data-testid="logout-btn"
      >
        <LogOut className="w-3.5 h-3.5 mr-2" />
        Se déconnecter
      </Button>
    </div>
  );
};

export default UserMenu;
